import type { CSSProperties } from "react";
import { brandFromModelName, isDarkHex, resolveBrand, type BrandGlyph } from "@/lib/brands";
import { cn } from "@/lib/utils";

type BrandIconProps = {
  /** Raw provider / host / lab name; resolved through the brand registry. */
  name?: string;
  /** Pre-resolved glyph, if you already have one. */
  glyph?: BrandGlyph;
  /** Resolve from a model id like "anthropic/claude-..." (lab owner wins). */
  modelName?: string;
  provider?: string;
  className?: string;
};

export function BrandIcon({ name, glyph, modelName, provider, className }: BrandIconProps) {
  const brand = glyph ?? (modelName ? brandFromModelName(modelName, provider) : resolveBrand(name ?? provider));
  if (brand.kind === "asset") return <img aria-hidden="true" alt="" data-brand={brand.title} title={brand.title} src={brand.src} loading="lazy" className={cn("size-4 shrink-0",className)} />;
  // Dark brand colours disappear against the dark UI, so let them inherit the
  // light foreground colour instead of painting themselves invisible.
  const tint = isDarkHex(brand.hex) ? undefined : brand.hex;

  if (brand.kind === "mono") {
    const style: CSSProperties = tint
      ? { backgroundColor: `color-mix(in srgb, ${tint} 18%, transparent)`, color: tint }
      : { backgroundColor: "var(--muted)", color: "var(--foreground)" };
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex size-4 shrink-0 items-center justify-center rounded-[0.28rem] text-[0.5rem] font-bold leading-none tracking-tight tabular-nums",
          className,
        )}
        data-brand={brand.title}
        style={style}
        title={brand.title}
      >
        {brand.label}
      </span>
    );
  }

  return (
    <svg
      aria-hidden="true"
      className={cn("size-4 shrink-0 fill-current", className)}
      data-brand={brand.title}
      role="img"
      style={tint ? { color: tint } : undefined}
      viewBox={brand.viewBox ?? "0 0 24 24"}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{brand.title}</title>
      <path d={brand.path} />
    </svg>
  );
}
